import { useState, useEffect } from "react";
import axios from "axios";

import { X, Mail, Lock, User, Loader2, ArrowLeft, CheckCircle, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthModal = ({ open, onOpenChange, onSuccess, translations: t, language = "ru" }) => {
  const [mode, setMode] = useState("login"); // login | register | forgot | reset_sent
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    rememberMe: true
  });

  const [errors, setErrors] = useState({});

  // Load saved email on mount and when modal opens
  useEffect(() => {
    if (open) {
      const savedEmail = localStorage.getItem("remembered_email");
      const wasRemembered = localStorage.getItem("remember_me") === "true";
      
      setFormData({ 
        email: wasRemembered && savedEmail ? savedEmail : "", 
        password: "", 
        name: "", 
        rememberMe: wasRemembered 
      });
      setErrors({});
      setMode("login");
    }
  }, [open]);

  // Save email when "remember me" is checked and login succeeds
  const saveRememberedEmail = (email, remember) => {
    if (remember) {
      localStorage.setItem("remembered_email", email);
      localStorage.setItem("remember_me", "true");
    } else {
      localStorage.removeItem("remembered_email");
      localStorage.setItem("remember_me", "false");
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = t?.email_required || "Email обязателен";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t?.email_invalid || "Неверный email";
    }
    
    if (mode !== "forgot") {
      if (!formData.password) {
        newErrors.password = t?.password_required || "Пароль обязателен";
      } else if (formData.password.length < 6) {
        newErrors.password = t?.password_min || "Минимум 6 символов";
      }
    }
    
    if (mode === "register" && !formData.name) {
      newErrors.name = t?.name_required || "Имя обязательно";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setLoading(true);
    
    try {
      if (mode === "forgot") {
        await axios.post(`${API}/auth/forgot-password`, { email: formData.email, lang: language });
        setMode("reset_sent");
        setLoading(false);
        return;
      }
      
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login" 
        ? { email: formData.email, password: formData.password, remember_me: formData.rememberMe, website: formData.website }
        : { ...formData, remember_me: formData.rememberMe, language: language, website: formData.website };
      
      const response = await axios.post(`${API}${endpoint}`, payload, { 
        withCredentials: true 
      });
      
      // Save tokens to localStorage as fallback for Safari ITP
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
      if (response.data.refresh_token) {
        localStorage.setItem('refresh_token', response.data.refresh_token);
      }
      
      // Save email if "remember me" is checked
      saveRememberedEmail(formData.email, formData.rememberMe);
      
      onSuccess(response.data.user);
      onOpenChange(false);
      toast.success(mode === "login" 
        ? (t?.login_success || "Добро пожаловать!") 
        : (t?.register_success || "Аккаунт создан! Проверьте почту.")
      );
    } catch (error) {
      console.error("Auth error:", error);
      console.error("Error response:", error.response);
      console.error("Error request:", error.request);
      console.error("Error config:", error.config);
      
      let message = t?.error || "Ошибка";
      const detail = error.response?.data?.detail || "";
      
      if (error.response?.status === 429) {
        message = t?.too_many_requests || "Слишком много попыток. Попробуйте позже.";
      } else if (detail.includes("already registered")) {
        message = t?.email_already_registered || "Этот email уже зарегистрирован";
      } else if (detail.includes("Invalid email or password")) {
        message = t?.invalid_credentials || "Неверный email или пароль";
      } else if (detail.includes("at least 6 characters")) {
        message = t?.password_too_short || "Пароль должен быть не менее 6 символов";
      } else if (detail) {
        message = detail;
      } else if (error.message === "Network Error") {
        message = t?.network_error || "Ошибка сети. Проверьте подключение.";
      } else if (error.code === "ERR_NETWORK") {
        message = t?.server_unavailable || "Сервер недоступен. Попробуйте позже.";
      } else if (error.response?.status === 500) {
        message = t?.server_error || "Ошибка сервера. Попробуйте позже.";
      } else if (error.response?.status === 404) {
        message = t?.endpoint_not_found || "API не найден. Обратитесь в поддержку.";
      }
      
      toast.error(message);
      
      if (detail.includes("already registered")) {
        setMode("login");
      }
    } finally {
      setLoading(false);
    }
  };

  // Detect mobile device
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
      || window.innerWidth < 768;
  };

  const handleGoogleAuth = () => {
    setGoogleLoading(true);
    
    const redirectUrl = window.location.origin + '/auth/callback';
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    
    // On mobile, use redirect instead of popup (popups are often blocked)
    if (isMobile()) {
      // Save current state to restore after redirect
      localStorage.setItem('auth_redirect_pending', 'true');
      window.location.href = authUrl;
      return;
    }
    
    // Desktop: Open Google auth in popup
    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    
    const popup = window.open(
      authUrl,
      'google_auth',
      `width=${width},height=${height},left=${left},top=${top},popup=1`
    );
    
    // If popup was blocked, fall back to redirect
    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      localStorage.setItem('auth_redirect_pending', 'true');
      window.location.href = authUrl;
      return;
    }
    
    // Listen for auth completion
    const checkPopup = setInterval(() => {
      try {
        if (!popup || popup.closed) {
          clearInterval(checkPopup);
          setGoogleLoading(false);
          return;
        }
        
        // Check if we got redirected back with session_id
        if (popup.location.href.includes('session_id=')) {
          const hash = popup.location.hash || popup.location.href;
          const sessionIdMatch = hash.match(/session_id=([^&]+)/);
          
          if (sessionIdMatch) {
            const sessionId = sessionIdMatch[1];
            popup.close();
            clearInterval(checkPopup);
            
            // Exchange session_id for session_token
            handleGoogleCallback(sessionId);
          }
        }
      } catch (e) {
        // Cross-origin error - popup is on different domain, continue waiting
      }
    }, 500);
  };

  const handleGoogleCallback = async (sessionId) => {
    try {
      const response = await axios.post(
        `${API}/auth/session`,
        { session_id: sessionId },
        { withCredentials: true }
      );
      
      // Save tokens to localStorage for Safari/auth persistence
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
      if (response.data.refresh_token) {
        localStorage.setItem('refresh_token', response.data.refresh_token);
      }
      
      onSuccess(response.data.user);
      onOpenChange(false);
      toast.success(t?.login_success || "Добро пожаловать!");
    } catch (error) {
      toast.error(t?.error_auth || "Ошибка авторизации");
    } finally {
      setGoogleLoading(false);
    }
  };

  // Listen for postMessage from popup
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'google_auth_success' && event.data?.sessionId) {
        handleGoogleCallback(event.data.sessionId);
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Password reset sent view
  if (mode === "reset_sent") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[420px] p-0 overflow-hidden bg-surface border-border/50" data-testid="auth-modal">
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-success/15 rounded-2xl flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="w-8 h-8 text-success" />
            </div>
            <h2 className="font-heading text-2xl font-bold tracking-tight mb-3">
              {t?.check_email || "Проверьте почту"}
            </h2>
            <p className="text-muted-foreground mb-6">
              {t?.reset_email_sent || "Мы отправили ссылку для сброса пароля на"} <strong className="text-foreground">{formData.email}</strong>
            </p>
            <Button
              variant="outline"
              className="w-full h-12 rounded-xl border-border/50 hover:border-primary/30"
              onClick={() => setMode("login")}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t?.back_to_login || "Вернуться к входу"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px] p-0 overflow-hidden bg-surface border-border/50" data-testid="auth-modal">
        <div className="p-8">
          {/* Header */}
          <div className="text-center mb-8">
            {mode === "forgot" ? (
              <>
                <h2 className="font-heading text-2xl font-bold tracking-tight mb-2">
                  {t?.forgot_password || "Забыли пароль?"}
                </h2>
                <p className="text-muted-foreground text-sm">
                  {t?.forgot_subtitle || "Введите email для восстановления"}
                </p>
              </>
            ) : (
              <>
                <h2 className="font-heading text-2xl font-bold tracking-tight mb-2">
                  {mode === "login" 
                    ? (t?.welcome_back || "С возвращением") 
                    : (t?.create_account || "Создать аккаунт")}
                </h2>
                <p className="text-muted-foreground text-sm">
                  {mode === "login" 
                    ? (t?.login_subtitle || "Войдите чтобы продолжить") 
                    : (t?.register_subtitle || "Присоединяйтесь бесплатно")}
                </p>
              </>
            )}
          </div>

          {/* Google Button (not for forgot password) */}
          {mode !== "forgot" && (
            <>
              <Button
                type="button"
                variant="outline"
                className="w-full h-12 mb-5 relative rounded-xl border-border/50 hover:border-primary/30 hover:bg-surface-2 transition-all"
                onClick={handleGoogleAuth}
                disabled={googleLoading || loading}
                data-testid="google-auth-btn"
              >
                {googleLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    {t?.continue_google || "Продолжить с Google"}
                  </>
                )}
              </Button>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border/50" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-surface px-3 text-muted-foreground">
                    {t?.or || "или"}
                  </span>
                </div>
              </div>
            </>
          )}

          {/* Email Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Back button for forgot password */}
            {mode === "forgot" && (
              <button
                type="button"
                className="flex items-center text-sm text-muted-foreground hover:text-primary transition-colors mb-2"
                onClick={() => setMode("login")}
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                {t?.back_to_login || "Назад"}
              </button>
            )}

            {/* Name field for registration */}
            {mode === "register" && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium text-muted-foreground">{t?.your_name || "Имя"}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="name"
                    type="text"
                    placeholder={t?.name_placeholder || "Ваше имя"}
                    className={`pl-10 h-12 bg-surface-2 border-border/50 rounded-xl ${errors.name ? 'border-destructive' : ''}`}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="name-input"
                  />
                </div>
                {errors.name && (
                  <p className="text-xs text-destructive">{errors.name}</p>
                )}
              </div>
            )}

            {/* Honeypot field - hidden from users, filled by bots */}
            <div className="absolute left-[-9999px]" aria-hidden="true">
              <input
                type="text"
                name="website"
                tabIndex="-1"
                autoComplete="off"
                value={formData.website || ""}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-muted-foreground">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="email@example.com"
                  className={`pl-10 h-12 bg-surface-2 border-border/50 rounded-xl ${errors.email ? 'border-destructive' : ''}`}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="email-input"
                />
              </div>
              {errors.email && (
                <p className="text-xs text-destructive">{errors.email}</p>
              )}
            </div>

            {mode !== "forgot" && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm font-medium text-muted-foreground">{t?.password || "Пароль"}</Label>
                  {mode === "login" && (
                    <button
                      type="button"
                      className="text-xs text-primary hover:underline"
                      onClick={() => setMode("forgot")}
                    >
                      {t?.forgot_password || "Забыли пароль?"}
                    </button>
                  )}
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    className={`pl-10 pr-10 h-12 bg-surface-2 border-border/50 rounded-xl ${errors.password ? 'border-destructive' : ''}`}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                    title={showPassword ? (t?.hide_password || "Скрыть пароль") : (t?.show_password || "Показать пароль")}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-xs text-destructive">{errors.password}</p>
                )}
              </div>
            )}

            {/* Remember me checkbox */}
            {mode !== "forgot" && (
              <div className="flex items-center space-x-2 pt-1">
                <Checkbox
                  id="rememberMe"
                  checked={formData.rememberMe}
                  onCheckedChange={(checked) => setFormData({ ...formData, rememberMe: checked })}
                  data-testid="remember-me-checkbox"
                  className="border-border/50 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                />
                <label
                  htmlFor="rememberMe"
                  className="text-sm text-muted-foreground cursor-pointer"
                >
                  {t?.remember_me || "Запомнить меня"}
                </label>
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-12 premium-btn rounded-xl text-white"
              disabled={loading || googleLoading}
              data-testid="submit-auth-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                mode === "forgot"
                  ? (t?.send_reset_link || "Отправить ссылку")
                  : mode === "login" 
                    ? (t?.sign_in || "Войти") 
                    : (t?.sign_up || "Зарегистрироваться")
              )}
            </Button>
          </form>

          {/* Toggle mode (not for forgot password) */}
          {mode !== "forgot" && (
            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">
                {mode === "login" 
                  ? (t?.no_account || "Нет аккаунта?") 
                  : (t?.have_account || "Уже есть аккаунт?")}
              </span>{" "}
              <button
                type="button"
                className="text-primary font-medium hover:underline"
                onClick={() => {
                  setMode(mode === "login" ? "register" : "login");
                  setErrors({});
                }}
                data-testid="toggle-auth-mode"
              >
                {mode === "login" 
                  ? (t?.sign_up || "Зарегистрироваться") 
                  : (t?.sign_in || "Войти")}
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
